<template>
    <component
        :is="resolvedIcon"
        v-if="resolvedIcon"
        :class="['inline-block align-middle', iconClass]"
        :size="pixelSize"
        :weight="iconWeight"
        @click="$emit('click', $event)"
    />
</template>

<script>
    import {
        PhArrowDown,
        PhArrowDownLeft,
        PhArrowDownRight,
        PhArrowElbowDownRight,
        PhArrowLeft,
        PhArrowRight,
        PhArrowSquareOut,
        PhArrowUp,
        PhArrowUpLeft,
        PhArrowUpRight,
        PhArrowsClockwise,
        PhArrowsCounterClockwise,
        PhArrowsIn,
        PhArrowsOut,
        PhBank,
        PhBell,
        PhBookOpen,
        PhBooks,
        PhBriefcase,
        PhBuildings,
        PhCalculator,
        PhCalendar,
        PhCalendarCheck,
        PhCalendarDots,
        PhCaretDown,
        PhCaretDoubleRight,
        PhCaretLeft,
        PhCaretRight,
        PhCaretUp,
        PhCaretUpDown,
        PhChartBar,
        PhChatCenteredText,
        PhCheck,
        PhCheckCircle,
        PhCircle,
        PhCircleNotch,
        PhClipboardText,
        PhClock,
        PhCopy,
        PhCube,
        PhCurrencyDollar,
        PhDeviceMobile,
        PhDeviceRotate,
        PhDiceThree,
        PhDotsThree,
        PhDotsThreeVertical,
        PhCreditCard,
        PhDownloadSimple,
        PhEnvelope,
        PhEnvelopeSimple,
        PhEquals,
        PhEye,
        PhEyeSlash,
        PhFileDoc,
        PhFileImage,
        PhFilePdf,
        PhFileMagnifyingGlass,
        PhFileText,
        PhFileZip,
        PhFire,
        PhFolder,
        PhFlag,
        PhGearSix,
        PhFlask,
        PhGraduationCap,
        PhHash,
        PhHeart,
        PhHouse,
        PhIdentificationCard,
        PhImage,
        PhInfo,
        PhKanban,
        PhLifebuoy,
        PhLightbulb,
        PhLightning,
        PhLink,
        PhLinkBreak,
        PhList,
        PhListBullets,
        PhLockKey,
        PhLockKeyOpen,
        PhMagnifyingGlass,
        PhMapPin,
        PhMinus,
        PhMonitor,
        PhPaperclip,
        PhPassword,
        PhPause,
        PhPencilLine,
        PhPencilSimple,
        PhPencilSimpleLine,
        PhPhone,
        PhPlay,
        PhPlus,
        PhPlusCircle,
        PhPuzzlePiece,
        PhQrCode,
        PhQuestion,
        PhScales,
        PhSealCheck,
        PhShareNetwork,
        PhShieldCheck,
        PhShoppingCart,
        PhSidebarSimple,
        PhSignIn,
        PhSignOut,
        PhSliders,
        PhSparkle,
        PhSpinner,
        PhSquaresFour,
        PhStack,
        PhStar,
        PhStorefront,
        PhSun,
        PhUploadSimple,
        PhTable,
        PhThumbsDown,
        PhThumbsUp,
        PhTimer,
        PhTrash,
        PhTrendUp,
        PhUser,
        PhUserCircle,
        PhUserGear,
        PhUserMinus,
        PhUserPlus,
        PhUsers,
        PhWarning,
        PhWarningCircle,
        PhX,
        PhXCircle,
    } from '@phosphor-icons/vue';
    import VIconHamilton from '@/components/elements/VIconHamilton.vue';
    import VIconValur from '@/components/elements/VIconValur.vue';

    const ICON_MAP = {
        'academic-hat': PhGraduationCap,
        adjustments: PhSliders,
        annotation: PhChatCenteredText,
        'arrow-down': PhArrowDown,
        'arrow-down-left': PhArrowDownLeft,
        'arrow-down-right': PhArrowDownRight,
        'arrow-elbow-down-right': PhArrowElbowDownRight,
        'arrow-left': PhArrowLeft,
        'arrow-right': PhArrowRight,
        'arrow-sm-down': PhCaretDown,
        'arrow-sm-up': PhCaretUp,
        'arrow-top-right': PhArrowUpRight,
        'arrow-top-right-on-square': PhArrowSquareOut,
        'arrow-up': PhArrowUp,
        'arrow-up-left': PhArrowUpLeft,
        'arrow-up-right': PhArrowUpRight,
        'arrows-in': PhArrowsIn,
        'arrows-counter-clockwise': PhArrowsCounterClockwise,
        'arrows-out': PhArrowsOut,
        bank: PhBank,
        bell: PhBell,
        'book-open': PhBookOpen,
        breaker: PhFlask,
        briefcase: PhBriefcase,
        'building-storefront': PhStorefront,
        calculator: PhCalculator,
        calendar: PhCalendar,
        'calendar-check': PhCalendarCheck,
        'calendar-dots': PhCalendarDots,
        'chart-bar': PhChartBar,
        check: PhCheck,
        'check-badge': PhSealCheck,
        'check-circle': PhCheckCircle,
        'chevron-down': PhCaretDown,
        'chevron-up': PhCaretUp,
        'chevron-left': PhCaretLeft,
        'chevron-double-right': PhCaretDoubleRight,
        'chevron-right': PhCaretRight,
        'caret-right': PhCaretRight,
        'chevron-up-down': PhCaretUpDown,
        circle: PhCircle,
        'circle-notch': PhCircleNotch,
        clock: PhClock,
        'clipboard-check': PhClipboardText,
        collection: PhSquaresFour,
        copy: PhCopy,
        cards: PhCreditCard,
        'credit-card': PhCreditCard,
        cube: PhCube,
        'currency-dollar': PhCurrencyDollar,
        dashboard: PhSquaresFour,
        'device-mobile': PhDeviceMobile,
        'device-rotate': PhDeviceRotate,
        'dice-three': PhDiceThree,
        'document-chart-bar': PhFileText,
        // A document under inspection: the ops validation tool reads a filing and compares it.
        'document-search': PhFileMagnifyingGlass,
        'document-text': PhFileText,
        file: PhFileText,
        'file-doc': PhFileDoc,
        'file-image': PhFileImage,
        'file-pdf': PhFilePdf,
        'file-zip': PhFileZip,
        folder: PhFolder,
        'list-bullets': PhListBullets,
        'dots-horizontal': PhDotsThree,
        'dots-vertical': PhDotsThreeVertical,
        download: PhDownloadSimple,
        envelope: PhEnvelope,
        'envelope-simple': PhEnvelopeSimple,
        equals: PhEquals,
        'exclamation-circle': PhWarningCircle,
        'exclamation-triangle': PhWarning,
        eye: PhEye,
        'eye-slash': PhEyeSlash,
        fire: PhFire,
        flag: PhFlag,
        gear: PhGearSix,
        hamilton: VIconHamilton,
        hamburger: PhList,
        hashtag: PhHash,
        heart: PhHeart,
        home: PhHouse,
        identification: PhIdentificationCard,
        image: PhImage,
        'information-circle': PhInfo,
        library: PhBooks,
        'light-bulb': PhLightbulb,
        'lightning-bolt': PhLightning,
        link: PhLink,
        'link-break': PhLinkBreak,
        'lock-key': PhLockKey,
        'lock-key-open': PhLockKeyOpen,
        login: PhSignIn,
        logout: PhSignOut,
        mail: PhEnvelope,
        'map-pin': PhMapPin,
        minus: PhMinus,
        monitor: PhMonitor,
        'office-building': PhBuildings,
        'paper-clip': PhPaperclip,
        password: PhPassword,
        'qr-code': PhQrCode,
        pause: PhPause,
        pencil: PhPencilSimple,
        'pencil-simple-line': PhPencilSimpleLine,
        'pencil-square': PhPencilLine,
        phone: PhPhone,
        play: PhPlay,
        plus: PhPlus,
        'plus-circle': PhPlusCircle,
        'puzzle-piece': PhPuzzlePiece,
        'question-mark-circle': PhQuestion,
        refresh: PhArrowsClockwise,
        scale: PhScales,
        search: PhMagnifyingGlass,
        share: PhShareNetwork,
        'shield-check': PhShieldCheck,
        'shopping-cart': PhShoppingCart,
        'sidebar-simple': PhSidebarSimple,
        sparkles: PhSparkle,
        spinner: PhSpinner,
        'square-3-stack': PhStack,
        star: PhStar,
        sun: PhSun,
        support: PhLifebuoy,
        table: PhTable,
        'thumbs-down': PhThumbsDown,
        'thumbs-up': PhThumbsUp,
        timer: PhTimer,
        trash: PhTrash,
        'trend-up': PhTrendUp,
        upload: PhUploadSimple,
        'upload-simple': PhUploadSimple,
        user: PhUser,
        'user-circle': PhUserCircle,
        'user-gear': PhUserGear,
        'user-group': PhUsers,
        'user-minus': PhUserMinus,
        'user-plus': PhUserPlus,
        valur: VIconValur,
        'view-boards': PhKanban,
        x: PhX,
        'x-circle': PhXCircle,
    };

    export default {
        props: {
            size: {
                default: 6,
                type: [Number, String],
            },
            strokeWidth: {
                default: '1.7',
                type: [Number, String],
            },
            type: {
                required: true,
                type: String,
            },
            weight: {
                default: 'regular',
                type: String,
                validator: v => ['thin', 'light', 'regular', 'bold', 'fill', 'duotone'].includes(v),
            },
        },
        emits: ['click'],
        computed: {
            resolvedIcon() {
                return ICON_MAP[this.type] || null;
            },
            pixelSize() {
                return Number(this.size) * 4;
            },
            iconWeight() {
                return this.weight;
            },
            iconClass() {
                if (this.type === 'spinner') return 'animate-spin';
                // The Valur logomark sits slightly below optical center inside its viewBox;
                // a small negative top margin nudges it up so it lines up with neighbouring
                // text/icons.
                if (this.type === 'valur') return '-mt-0.5';
                return '';
            },
        },
    };
</script>
